import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import MainLayout from "@/components/layouts";
import { FormCreator, FormFieldDef } from "@/components/shared/form-creator";
import { changePassword } from "./change-password.api";

const formSchema = z
  .object({
    currentPassword: z.string({ required_error: "Current password is required" }).min(1, "Current password is required"),
    newPassword: z.string({ required_error: "New password is required" }).min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string({ required_error: "Please confirm your new password" }),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type FormValues = z.infer<typeof formSchema>;

const formDefinition: FormFieldDef[] = [
  {
    type: "password",
    name: "currentPassword",
    label: "Current password",
    placeholder: "Enter your current password",
    required: true,
  },
  {
    type: "password",
    name: "newPassword",
    label: "New password",
    placeholder: "Enter your new password",
    required: true,
  },
  {
    type: "password",
    name: "confirmPassword",
    label: "Confirm new password",
    placeholder: "Confirm your new password",
    required: true,
  },
];

export default function ChangePasswordPage() {
  const [loading, setLoading] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setLoading(true);
    try {
      await changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      });
      toast.success("Your password has been updated.");
      form.reset();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to change password.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  });

  const breadcrumb = [
    { name: "Settings", href: "#" },
    { name: "Change password", href: "#" },
  ];

  return (
    <MainLayout
      breadcrumb={breadcrumb}
      pageHeader={{
        title: "Change password",
        subtitle: "Update your password. Use at least 6 characters.",
      }}
    >
      <div className="container mx-auto max-w-md">
        <FormCreator
          form={form}
          onSubmit={onSubmit}
          formDefinition={formDefinition}
          buttonText={loading ? "Updating…" : "Update password"}
          formClassName="space-y-6"
        />
      </div>
    </MainLayout>
  );
}
